import api from 'api/api'
import {
    confirmShadowCleanup,
    fetchPendingShadowProbes,
    lookUpServerShare,
    fetchShadowProbe,
    reportShadowEvent,
    reportShadowProbeResult,
} from 'api/localSigningShadowApi'
import {
    clearClientShare,
    clearStoredClientSigningDeviceId,
    getClientShare,
    getKeyInfo,
    getLocalSelfCustodyPayloadStatus,
    getOrCreateLocalDeviceId,
    getStoredClientSigningDeviceId,
    persistSelfCustodyWalletForDevice,
    recoverLocalWalletTextWithShares,
    recoverPaymentKeyWifWithShares,
} from 'lib/bsv-signing'
import { requestPersistentStorage } from 'lib/storage-persistence'
import { resignOwnInputs, resignPostAip, transactionFromBeefHex } from 'lib/localSigningShadowSign'
import { CLIENT_SIGNING_READY_EVENT } from 'lib/clientSigningFeature'
import { recoverPromotedDeviceKeys } from 'wallets/promotedDeviceRecovery'

// Local signing shadow mode (app/services/local_signing_shadow.rb), silent and
// best-effort: keep a copy of this user's Treechat-held wallet on the device, re-sign
// the server's landed transactions with it (never broadcast) and report how that went.
// Nothing here may surface UI or interfere with the app; every failure is a report.

/** Dispatched by the user channel when the server has a transaction to re-sign. */
export const SHADOW_PROBE_EVENT = 'treechat:local-signing-shadow-probe'

type Fields = Record<string, string | number | boolean | null | undefined>

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

function errorFields(error: unknown): Fields {
    // Messages come from our own code and the browser; wallet material never reaches them.
    const err = error instanceof Error ? error : new Error(String(error))
    return { error_name: err.name, error: err.message.slice(0, 300) }
}

function environment(): Fields {
    const standalone =
        (navigator as Navigator & { standalone?: boolean }).standalone === true ||
        (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches)
    return {
        user_agent: navigator.userAgent.slice(0, 300),
        standalone,
        visible: typeof document !== 'undefined' && document.visibilityState === 'visible',
        online: navigator.onLine,
    }
}

async function storageFacts(): Promise<Fields> {
    const storage = navigator.storage
    const facts: Fields = {}
    try {
        if (storage?.persisted) facts.persisted = await storage.persisted()
        if (storage?.estimate) {
            const estimate = await storage.estimate()
            facts.quota_bytes = estimate.quota
            facts.usage_bytes = estimate.usage
        }
    } catch (error) {
        facts.reason = `storage facts unavailable: ${(error as Error)?.message || error}`.slice(
            0,
            300,
        )
    }
    return facts
}

// Firefox asks the person before granting persistent storage; shadow mode must stay
// silent there. Chrome and Safari decide without a prompt.
function isFirefox() {
    return /Firefox\//.test(navigator.userAgent)
}

function report(
    kind: Parameters<typeof reportShadowEvent>[0],
    deviceId: string | null,
    data: Fields,
) {
    return reportShadowEvent(kind, deviceId, data).catch(() => {})
}

// One tab at a time per browser. A probe another tab is already answering is skipped;
// setup waits its turn, so a second tab sees the keys the first one just stored.
async function exclusive<T>(
    name: string,
    work: () => Promise<T>,
    { wait = false }: { wait?: boolean } = {},
): Promise<T | undefined> {
    const locks = navigator.locks
    if (!locks?.request) return work()
    if (wait) return locks.request(name, async () => work())
    return locks.request(name, { ifAvailable: true }, async lock => (lock ? work() : undefined))
}

/**
 * On every load: does this browser still hold the shadow keys? Missing keys after they
 * were set up mean the browser dropped them (storage eviction, cleared site data, Safari's
 * seven-day limit), which is what real device signing would run into.
 */
export async function checkShadowKeys(
    userId: string,
    source = 'load',
): Promise<'present' | 'missing' | 'legacy' | 'unknown'> {
    const share = await getClientShare(userId).catch(() => null)
    const payloadStatus = await getLocalSelfCustodyPayloadStatus(userId).catch(
        () => 'missing' as const,
    )
    let lookup: Awaited<ReturnType<typeof lookUpServerShare>> | null = null
    if (share) {
        lookup = await lookUpServerShare(share.deviceId)
        if (lookup.status === 'unknown') {
            await new Promise(resolve => setTimeout(resolve, 2_000))
            lookup = await lookUpServerShare(share.deviceId)
        }
    }
    let reason: string | undefined
    let state: 'present' | 'missing' | 'legacy' | 'unknown'
    if (!share) state = 'missing'
    // Couldn't reach Treechat: keep the keys as they are and look again next load.
    else if (lookup?.status === 'unknown') state = 'unknown'
    else if (lookup?.status !== 'present') state = 'missing'
    else state = payloadStatus === 'compatible' ? 'present' : payloadStatus
    if (state === 'present' && share && lookup?.status === 'present') {
        // The pieces must still open the wallet: an interrupted setup can leave this
        // device's share and Treechat's from different splits.
        try {
            await recoverPaymentKeyWifWithShares(
                userId,
                lookup.treechatShare,
                share.clientShare,
                share.address,
            )
        } catch (error) {
            state = 'missing'
            reason = `shares do not open the wallet: ${errorFields(error).error}`
        }
    }
    // Chrome decides persistence from how the site is used and can say yes on a later
    // visit, so a device whose storage isn't persisted yet asks again on every load.
    // persist_result=granted here means the answer changed since setup.
    let persistResult: string | undefined
    if (share && !isFirefox() && navigator.storage?.persisted) {
        const alreadyPersisted = await navigator.storage.persisted().catch(() => true)
        if (!alreadyPersisted) persistResult = (await requestPersistentStorage()).status
    }
    const storedDeviceId = getStoredClientSigningDeviceId(userId)
    await report('key_check', share?.deviceId || null, {
        source,
        persist_result: persistResult,
        // A device id this browser still sends with requests, and whether it is the
        // device that holds the share (a lost share leaves the id in localStorage).
        stored_device: Boolean(storedDeviceId),
        stored_device_matches: storedDeviceId ? storedDeviceId === share?.deviceId : null,
        client_share: Boolean(share),
        payload_status: payloadStatus,
        server_share: lookup ? lookup.status : null,
        ok: state === 'present',
        reason,
        ...(await storageFacts()),
        ...environment(),
    })
    return state
}

/**
 * Put a copy of the Treechat-held wallet on this device the way device setup does
 * (client share + encrypted wallet in IndexedDB, Treechat's share on the server), with
 * every step timed and reported. The download is marked so it doesn't count as a backup.
 */
export async function bootstrapShadowKeys(userId: string, source = 'shadow'): Promise<boolean> {
    let step = 'start'
    let deviceId: string | null = null
    const timed = async <T>(name: string, work: () => Promise<T>): Promise<T> => {
        step = name
        const started = now()
        const result = await work()
        await report('bootstrap', deviceId, {
            source,
            step: name,
            ok: true,
            ms: Math.round(now() - started),
        })
        return result
    }
    try {
        const walletText = await timed('download', async () =>
            (await api.downloadBsvWallet('local_signing_shadow')).text(),
        )
        deviceId = await timed('device_id', () => getOrCreateLocalDeviceId(userId))
        const persisted = await timed('persist', () =>
            persistSelfCustodyWalletForDevice(userId, deviceId!, walletText, {
                requestPersistence: false,
            }),
        )
        await timed('verify_read_back', async () => {
            const stored = await getClientShare(userId)
            if (!stored || stored.deviceId !== deviceId)
                throw new Error('Client share not found after writing it')
            if (stored.clientShare !== persisted.clientShareRecord.clientShare) {
                throw new Error('Client share read back differs from the one written')
            }
            const payloadStatus = await getLocalSelfCustodyPayloadStatus(userId)
            if (payloadStatus !== 'compatible')
                throw new Error(`Wallet copy read back as ${payloadStatus}`)
        })
        await timed('register_server_share', async () => {
            const result = await api.registerBsvServerShare(
                deviceId!,
                persisted.shares.treechatShare,
                persisted.walletIdentity,
            )
            if (!result.success) throw new Error(result.error || 'Server share registration failed')
        })
        await timed('verify_server_share', async () => {
            const serverShare = await api.getBsvServerShare(userId, deviceId!)
            if (serverShare?.treechatShare !== persisted.shares.treechatShare) {
                throw new Error('Server share read back differs from the one registered')
            }
        })
        const persistence = isFirefox()
            ? { status: 'skipped_firefox' }
            : await timed('persist_storage', () => requestPersistentStorage())
        await report('bootstrap', deviceId, {
            source,
            step: 'done',
            ok: true,
            persist_result: persistence?.status,
            ...(await storageFacts()),
            ...environment(),
        })
        return true
    } catch (error) {
        await report('bootstrap_failed', deviceId, {
            source,
            step,
            ...errorFields(error),
            ...environment(),
        })
        return false
    }
}

// A probe is done once its result reached the server; until then the catch-up polls may
// retry it (a dropped connection, or a tab that closed mid-way). In-flight work is only
// guarded against running twice at once.
const deliveredProbes = new Set<string>()
const inFlightProbes = new Set<string>()
// Probes that arrive while this page is still setting the keys up wait for it, so a
// fresh page isn't scored as "no device key". The wait is capped: setup can stall behind
// another tab (a frozen one holding the setup lock), and the keys may already be here.
let setupFinished: Promise<void> = Promise.resolve()
const SETUP_WAIT_CAP_MS = 15_000

async function waitForSetup(): Promise<boolean> {
    let timer: ReturnType<typeof setTimeout> | undefined
    const capped = new Promise<false>(resolve => {
        timer = setTimeout(() => resolve(false), SETUP_WAIT_CAP_MS)
    })
    const finished = await Promise.race([setupFinished.then(() => true as const), capped])
    clearTimeout(timer)
    return finished
}

/**
 * Re-sign one server-signed transaction and report the txid (never broadcast). Every
 * probe this page receives is reported (probe_received), and so is every way it can
 * end without an answer (probe_skipped), so a device that was online but didn't answer
 * shows why. The reports never wait: they must not eat the probe's answer window.
 */
export async function processShadowProbe(
    userId: string,
    probeId: string,
    source: 'push' | 'catch_up' = 'push',
): Promise<void> {
    if (deliveredProbes.has(probeId) || inFlightProbes.has(probeId)) return
    inFlightProbes.add(probeId)
    const started = now()
    const visible = typeof document !== 'undefined' && document.visibilityState === 'visible'
    void report('probe_received', null, { probe_id: probeId, source, visible })
    try {
        if (!(await waitForSetup())) {
            // Setup is still running: answer only with keys that are already complete
            // here and at Treechat. Otherwise leave the probe for the next catch-up,
            // rather than scoring a half-set-up device.
            const share = await getClientShare(userId).catch(() => null)
            let ready = false
            if (share) {
                const lookup = await lookUpServerShare(share.deviceId)
                // A repair can stall between writing new local shares and registering
                // Treechat's: the pieces must still open the wallet.
                ready =
                    lookup.status === 'present' &&
                    (await recoverPaymentKeyWifWithShares(
                        userId,
                        lookup.treechatShare,
                        share.clientShare,
                        share.address,
                    ).then(
                        () => true,
                        () => false,
                    ))
            }
            void report('probe_skipped', share?.deviceId || null, {
                probe_id: probeId,
                source,
                reason: ready ? 'setup_wait_capped_answering' : 'setup_wait_capped',
                ms: SETUP_WAIT_CAP_MS,
            })
            if (!ready) return
        }
        const delivered = await exclusive(`local-signing-shadow-probe:${probeId}`, () =>
            answerShadowProbe(userId, probeId),
        )
        if (delivered) deliveredProbes.add(probeId)
        else
            void report('probe_skipped', null, {
                probe_id: probeId,
                source,
                // undefined: another tab of this browser holds the probe's lock.
                reason: delivered === undefined ? 'other_tab_answering' : 'not_delivered',
                ms: Math.round(now() - started),
            })
    } catch (error) {
        void report('probe_skipped', null, {
            probe_id: probeId,
            source,
            reason: 'exception',
            ms: Math.round(now() - started),
            ...errorFields(error),
        })
    } finally {
        inFlightProbes.delete(probeId)
    }
}

/** The wallet's payment key (checked against this device's address) and ordinal key:
 * the keys Treechat signs a custodial user's transactions with. */
async function recoverShadowSigningKeys(
    userId: string,
    treechatShare: string,
    clientShare: string,
    address: string,
): Promise<string[]> {
    const wallet = JSON.parse(
        await recoverLocalWalletTextWithShares(userId, treechatShare, clientShare),
    )
    if (!wallet?.payPk) throw new Error('Recovered wallet JSON is missing payPk')
    if (getKeyInfo(wallet.payPk)?.address !== address) {
        throw new Error('Recovered wallet payment key does not match the expected address')
    }
    return wallet.ordPk ? [wallet.payPk, wallet.ordPk] : [wallet.payPk]
}

/**
 * The probe kinds this re-signer understands, sent with every answer. A tab still
 * running older code would score kinds it can't sign as failures, so the server only
 * scores kinds this version knows (LocalSigningShadow::SIGNER_VERSION_KINDS):
 * 1 = upvalues and payments; 2 = also both wallet keys, the server's sighash flags and
 * posts' AIP author tuples (usage fees, Write Onchain, mints, ordinal sends, settlements,
 * posts).
 */
export const SHADOW_SIGNER_VERSION = 2

/** True when a result (success or failure) reached the server. */
async function answerShadowProbe(userId: string, probeId: string): Promise<boolean> {
    const deliver = (
        result: Omit<Parameters<typeof reportShadowProbeResult>[1], 'signer_version'>,
    ) =>
        reportShadowProbeResult(probeId, { ...result, signer_version: SHADOW_SIGNER_VERSION }).then(
            () => true,
            () => false,
        )
    const share = await getClientShare(userId).catch(() => null)
    const visible = typeof document !== 'undefined' && document.visibilityState === 'visible'
    if (!share) {
        // Not this browser's to score: another of the user's devices may still answer, and
        // a probe no device answers stays "sent", just as a real request would expire.
        await report('probe_error', null, { probe_id: probeId, reason: 'no_device_key', visible })
        return true
    }
    let fetchMs: number | undefined
    let beef: string
    let kind: string
    try {
        const fetchStarted = now()
        const fetched = await fetchShadowProbe(probeId)
        beef = fetched.beef
        kind = fetched.probe.kind
        fetchMs = Math.round(now() - fetchStarted)
    } catch (error) {
        // The transaction or a parent may not be fetchable yet; that says nothing about
        // device signing, so leave the probe for the next catch-up.
        await report('probe_error', share.deviceId, {
            probe_id: probeId,
            reason: 'fetch_unavailable',
            ...errorFields(error),
        })
        return false
    }
    try {
        const signStarted = now()
        const serverShare = await api.getBsvServerShare(userId, share.deviceId)
        if (!serverShare) throw new Error('Treechat holds no share for this device')
        const keyWifs = await recoverShadowSigningKeys(
            userId,
            serverShare.treechatShare,
            share.clientShare,
            share.address,
        )
        const tx = transactionFromBeefHex(beef)
        // A post's inputs are the platform's; the user's key signed its AIP author tuple.
        const signed =
            kind === 'post' ? resignPostAip(tx, keyWifs[0]) : await resignOwnInputs(tx, keyWifs)
        return deliver({
            device_id: share.deviceId,
            device_txid: signed.txid,
            sign_ms: Math.round(now() - signStarted),
            fetch_ms: fetchMs,
            signed_inputs: signed.signedInputs,
            foreign_inputs: signed.foreignInputs,
            visible,
        })
    } catch (error) {
        const fields = errorFields(error)
        const delivered = await deliver({
            device_id: share.deviceId,
            fetch_ms: fetchMs,
            visible,
            error: `${fields.error_name}: ${fields.error}`,
        })
        if (!delivered)
            await report('probe_error', share.deviceId, { probe_id: probeId, ...fields })
        return delivered
    }
}

/** Probes sent while this browser was closed (a real signing request waits 10 minutes). */
export async function pickUpPendingShadowProbes(userId: string): Promise<void> {
    const probes = await fetchPendingShadowProbes().catch(() => [])
    for (const probe of probes) await processShadowProbe(userId, probe.id, 'catch_up')
}

const BOOTSTRAP_FAILED_KEY = 'treechat-local-signing-shadow-bootstrap-failed:'

// After a failed setup, no retry until the next browser session: where storage is
// blocked (some private modes) every page load would otherwise download the wallet again
// only to fail again. A successful setup sets nothing, so later repairs still run.
function bootstrapFailedThisSession(userId: string): boolean {
    try {
        return Boolean(sessionStorage.getItem(BOOTSTRAP_FAILED_KEY + userId))
    } catch (_error) {
        return false
    }
}

function setBootstrapFailed(userId: string, failed: boolean) {
    try {
        if (failed) sessionStorage.setItem(BOOTSTRAP_FAILED_KEY + userId, '1')
        else sessionStorage.removeItem(BOOTSTRAP_FAILED_KEY + userId)
    } catch (_error) {
        // Without session storage: one attempt per page load.
    }
}

const CLEANUP_PENDING_KEY = 'treechat-local-signing-shadow-cleanup:'

function pendingCleanup(userId: string): string | null {
    try {
        return localStorage.getItem(CLEANUP_PENDING_KEY + userId)
    } catch (_error) {
        return null
    }
}

function setPendingCleanup(userId: string, deviceId: string | null) {
    try {
        if (deviceId) localStorage.setItem(CLEANUP_PENDING_KEY + userId, deviceId)
        else localStorage.removeItem(CLEANUP_PENDING_KEY + userId)
    } catch (_error) {
        // Without storage a lost confirmation simply isn't retried.
    }
}

/**
 * Keys revoked on request (rake local_signing_shadow:revoke): Treechat's share is already
 * gone, so this copy can't be opened; delete it and confirm. Only this browser's own
 * device is touched, only when the server lists it and still holds no share for it (a
 * tab that already set up fresh keys under the same device id keeps them). Runs under
 * the setup lock so it never interleaves with another tab's setup.
 */
export async function removeRevokedShadowKeys(
    userId: string,
    revokedDeviceIds: string[],
): Promise<void> {
    if (revokedDeviceIds.length === 0) return
    await exclusive(
        'local-signing-shadow-setup',
        async () => {
            const share = await getClientShare(userId)
            // A deletion whose confirmation didn't reach the server last time; confirmed
            // only once the keys are verifiably gone from this browser.
            const pending = pendingCleanup(userId)
            if (pending && revokedDeviceIds.includes(pending) && share?.deviceId !== pending) {
                await confirmShadowCleanup(pending)
                setPendingCleanup(userId, null)
            }

            if (!share || !revokedDeviceIds.includes(share.deviceId)) return
            const lookup = await lookUpServerShare(share.deviceId)
            // 'present': another tab already set up fresh keys under this id. 'unknown':
            // couldn't ask. 'forbidden' (no longer enrolled) can't have re-registered.
            if (lookup.status === 'present' || lookup.status === 'unknown') return
            setPendingCleanup(userId, share.deviceId)
            await clearClientShare(userId)
            if (await getClientShare(userId))
                throw new Error('Revoked keys are still on this device')
            await confirmShadowCleanup(share.deviceId)
            setPendingCleanup(userId, null)
            // Still enrolled: let the next setup run right away.
            setBootstrapFailed(userId, false)
        },
        { wait: true },
    )
}

/** App start: check the keys, set them up when missing, then catch up on probes. */
export async function startLocalSigningShadow(userId: string): Promise<void> {
    setupFinished = (async () => {
        await exclusive(
            'local-signing-shadow-setup',
            async () => {
                const state = await checkShadowKeys(userId)
                const repair = state === 'missing' || state === 'legacy'
                if (repair && !bootstrapFailedThisSession(userId)) {
                    setBootstrapFailed(userId, !(await bootstrapShadowKeys(userId)))
                }
            },
            { wait: true },
        )
    })().catch(() => {})
    await setupFinished
    await pickUpPendingShadowProbes(userId)
}

/**
 * App start for a promoted user (gon.localSigningShareRecovery): this device signs for
 * real while Treechat still holds the wallet. Keys this browser lost are set up again
 * from Treechat's copy; if that fails the stale device id is forgotten so Treechat signs.
 */
export async function startPromotedDeviceRecovery(userId: string): Promise<void> {
    await exclusive(
        'local-signing-shadow-setup',
        () =>
            recoverPromotedDeviceKeys({
                storedDeviceId: () => getStoredClientSigningDeviceId(userId),
                checkKeys: () => checkShadowKeys(userId, 'promoted_load'),
                setUpKeys: () => bootstrapShadowKeys(userId, 'promoted_recovery'),
                forgetDevice: async () => {
                    // getClientShare re-saves the id of any share left behind.
                    await clearClientShare(userId).catch(() => false)
                    clearStoredClientSigningDeviceId(userId)
                },
                signalReady: () => window.dispatchEvent(new Event(CLIENT_SIGNING_READY_EVENT)),
                failedThisSession: () => bootstrapFailedThisSession(userId),
                setFailed: failed => setBootstrapFailed(userId, failed),
                report: async fields => {
                    await report('key_check', getStoredClientSigningDeviceId(userId), {
                        ...fields,
                        ...(await storageFacts()),
                        ...environment(),
                    })
                },
            }),
        { wait: true },
    )
}
